import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Btn, Modal, FormRow, Card } from '../components/UI';

const MAPBOX_TOKEN_KEY = 'koo-yong-mapbox-token';
const DEFAULT_MAPBOX_TOKEN = 'pk.eyJ1IjoiYmVuaGF6ZSIsImEiOiJjbXJ5dmd6cHgwMm9wMnpyMHE3cWE2bGx3In0.-dzGnJhRBaTGCromnupusA';
const KOO_YONG_LNG = -3.751522;
const KOO_YONG_LAT = 50.45979;

function TreeMarker({ marker, selected, onClick }) {
  return (
    <div onClick={e => { e.stopPropagation(); onClick(marker); }}
      style={{
        position: 'absolute',
        left: marker.x - 10,
        top: marker.y - 10,
        width: 20,
        height: 20,
        cursor: 'pointer',
        zIndex: selected ? 10 : 5,
      }}>
      <div style={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        background: selected ? 'var(--accent)' : 'var(--bg-card)',
        border: `1.5px solid ${selected ? 'var(--accent)' : 'var(--moss-400)'}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        boxShadow: selected ? '0 0 0 2px rgba(61,107,34,0.25)' : 'none',
        transition: 'all 0.15s',
      }}>🌳</div>
      <div style={{
        position: 'absolute',
        top: 22,
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'var(--stone-900)',
        color: 'var(--stone-50)',
        fontSize: 9,
        padding: '1px 5px',
        borderRadius: 4,
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
      }}>{marker.name}</div>
    </div>
  );
}

function MapboxSetup({ onSave }) {
  const [token, setToken] = useState('');
  return (
    <div style={{ maxWidth: 500, margin: '0 auto', padding: '2rem' }}>
      <Card>
        <h2 style={{ fontSize: 18, marginBottom: '0.5rem' }}>Connect Mapbox</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
          To show a real aerial view of your plot, you need a free Mapbox API key.
          Create one at <a href="https://account.mapbox.com/access-tokens/" target="_blank" rel="noreferrer">mapbox.com</a> — the free tier covers thousands of loads per month.
        </p>
        <FormRow label="Mapbox public token">
          <input value={token} onChange={e => setToken(e.target.value)} placeholder="pk.eyJ1IjoiL…" />
        </FormRow>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="primary" onClick={() => onSave(token)} disabled={!token.startsWith('pk.')}>Save & load map</Btn>
        </div>
      </Card>
      <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--bg-subtle)', borderRadius: 'var(--radius-md)', fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
        <strong>Steps:</strong><br />
        1. Go to mapbox.com and create a free account<br />
        2. Visit Account → Access tokens<br />
        3. Copy your default public token (starts with pk.)<br />
        4. Paste it above
      </div>
    </div>
  );
}

function AddTreeModal({ plants, onSave, onClose, position }) {
  const [form, setForm] = useState({ name: '', plantId: '', notes: '' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const trees = plants.filter(p => p.type === 'Tree');

  function handleSave() {
    if (!form.name.trim()) return;
    const linked = trees.find(p => p.id === form.plantId);
    onSave({
      id: 'tm_' + Date.now(),
      name: form.name,
      plantId: form.plantId,
      notes: form.notes,
      lng: position.lng,
      lat: position.lat,
      image: linked?.image || null,
    });
  }

  return (
    <Modal title="Place tree" onClose={onClose} width={380}>
      <FormRow label="Tree name"><input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Old Apple" autoFocus /></FormRow>
      <FormRow label="Link to plant journal" hint="Optional — links this marker to a plant entry">
        <select value={form.plantId} onChange={e => {
          set('plantId', e.target.value);
          const p = trees.find(t => t.id === e.target.value);
          if (p && !form.name) set('name', p.name);
        }}>
          <option value="">— Not linked —</option>
          {trees.map(p => <option key={p.id} value={p.id}>{p.name} {p.variety ? `(${p.variety})` : ''}</option>)}
        </select>
      </FormRow>
      <FormRow label="Notes"><textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Variety, age, notes…" /></FormRow>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" onClick={handleSave} disabled={!form.name.trim()}>Place tree</Btn>
      </div>
    </Modal>
  );
}

function TreeDetail({ marker, plant, onDelete, onClose }) {
  return (
    <div style={{
      position: 'absolute', right: 16, top: 16, width: 240,
      background: 'var(--bg-card)', border: '1px solid var(--border-md)',
      borderRadius: 'var(--radius-lg)', overflow: 'hidden', zIndex: 20,
      boxShadow: '0 4px 24px rgba(30,27,23,0.12)',
    }}>
      {(plant?.image || marker.image) && (
        <img src={plant?.image || marker.image} alt={marker.name}
          style={{ width: '100%', height: 120, objectFit: 'cover' }} />
      )}
      <div style={{ padding: '0.875rem 1rem' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 500, marginBottom: 4 }}>{marker.name}</div>
        {plant && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>Linked: {plant.name} {plant.variety || ''}</div>}
        {marker.notes && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>{marker.notes}</div>}
        <div style={{ display: 'flex', gap: 6 }}>
          <Btn size="sm" variant="danger" onClick={() => onDelete(marker.id)}>Remove</Btn>
          <Btn size="sm" onClick={onClose}>Close</Btn>
        </div>
      </div>
    </div>
  );
}

export default function TreeMap({ state, dispatch }) {
  const [mapboxToken, setMapboxToken] = useState(() => localStorage.getItem(MAPBOX_TOKEN_KEY) || DEFAULT_MAPBOX_TOKEN);
  const [mode, setMode] = useState('view');
  const [placing, setPlacing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [bgImage, setBgImage] = useState(state.treeMapBackground || null);
  const [showBgUpload, setShowBgUpload] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapTick, setMapTick] = useState(0);
  const mapRef = useRef(null);
  const mapboxInstance = useRef(null);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const migratedRef = useRef(false);

  const markers = state.treeMarkers || [];

  // Screen position for each marker, computed live from its real-world lng/lat
  // so markers stay put no matter how the map is zoomed, panned, or resized.
  // Legacy markers that only have old screen-pixel x/y (pre-migration) fall back to those.
  const renderedMarkers = useMemo(() => markers.map(m => {
    if (m.lng != null && m.lat != null && mapboxInstance.current && mapLoaded) {
      const p = mapboxInstance.current.project([m.lng, m.lat]);
      return { ...m, x: p.x, y: p.y };
    }
    return m;
  }), [markers, mapLoaded, mapTick]);

  function saveToken(token) {
    localStorage.setItem(MAPBOX_TOKEN_KEY, token);
    setMapboxToken(token);
  }

  useEffect(() => {
    if (!mapboxToken || mapboxInstance.current) return;
    const script = document.createElement('script');
    script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js';
    script.onload = () => {
      const mapboxgl = window.mapboxgl;
      mapboxgl.accessToken = mapboxToken;
      const map = new mapboxgl.Map({
        container: mapRef.current,
        style: 'mapbox://styles/mapbox/satellite-streets-v12',
        center: [KOO_YONG_LNG, KOO_YONG_LAT],
        zoom: 18,
      });
      map.on('load', () => setMapLoaded(true));
      map.on('move', () => setMapTick(t => t + 1));
      map.on('resize', () => setMapTick(t => t + 1));
      mapboxInstance.current = map;

      // Mapbox's canvas can visually stretch to fill a resized container
      // without its internal transform actually updating, which throws off
      // project()/unproject() math. Force an explicit resize sync whenever
      // the container's actual size changes (window resize, sidebar toggle,
      // font-size-driven reflow, etc.) using ResizeObserver rather than
      // relying only on the window 'resize' event.
      const syncResize = () => {
        if (!mapboxInstance.current) return;
        mapboxInstance.current.resize();
        // Don't rely on Mapbox's own 'resize'/'move' events firing after a
        // manual resize() call — force the marker recompute directly.
        setMapTick(t => t + 1);
      };
      const ro = new ResizeObserver(syncResize);
      ro.observe(mapRef.current);
      window.addEventListener('resize', syncResize);
      mapboxInstance.current._koResizeObserver = ro;
      mapboxInstance.current._koWindowResizeHandler = syncResize;
    };
    document.head.appendChild(script);

    return () => {
      if (mapboxInstance.current?._koResizeObserver) {
        mapboxInstance.current._koResizeObserver.disconnect();
      }
      if (mapboxInstance.current?._koWindowResizeHandler) {
        window.removeEventListener('resize', mapboxInstance.current._koWindowResizeHandler);
      }
    };
  }, [mapboxToken]);

  // One-time migration: convert any older markers that only have screen-pixel
  // x/y (captured at this exact zoom/view) into real lng/lat so they stay
  // correctly placed even if the map is later resized or zoomed.
  useEffect(() => {
    if (!mapLoaded || migratedRef.current || !mapboxInstance.current) return;
    const legacy = markers.filter(m => (m.lng == null || m.lat == null) && m.x != null && m.y != null);
    if (legacy.length === 0) { migratedRef.current = true; return; }
    legacy.forEach(m => {
      const { lng, lat } = mapboxInstance.current.unproject([m.x, m.y]);
      dispatch({ type: 'UPDATE_TREE_MARKER', id: m.id, updates: { lng, lat } });
    });
    migratedRef.current = true;
  }, [mapLoaded, markers, dispatch]);

  function handleCanvasClick(e) {
    if (mode !== 'place' || !mapboxInstance.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const { lng, lat } = mapboxInstance.current.unproject([x, y]);
    setPlacing({ x, y, lng, lat });
  }

  function handleBgUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const url = ev.target.result;
      setBgImage(url);
      dispatch({ type: 'SET_TREE_MAP_BG', url });
      setShowBgUpload(false);
    };
    reader.readAsDataURL(file);
  }

  const selectedMarker = markers.find(m => m.id === selected);
  const selectedPlant = selectedMarker?.plantId ? state.plants.find(p => p.id === selectedMarker.plantId) : null;

  if (!mapboxToken) return <MapboxSetup onSave={saveToken} />;

  return (
    <div style={{ padding: '2rem' }} className="fade-up">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
        <div>
          <h1 style={{ fontSize: 24, marginBottom: 4 }}>Tree map</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{markers.length} tree{markers.length !== 1 ? 's' : ''} mapped</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Btn size="sm" onClick={() => setShowBgUpload(true)}>↑ Upload site plan</Btn>
          <Btn size="sm"
            variant={mode === 'place' ? 'primary' : 'outline'}
            onClick={() => setMode(m => m === 'place' ? 'view' : 'place')}>
            {mode === 'place' ? '✕ Cancel placing' : '+ Place tree'}
          </Btn>
        </div>
      </div>

      {mode === 'place' && (
        <div style={{ fontSize: 13, color: 'var(--moss-600)', background: 'var(--moss-50)', padding: '8px 14px', borderRadius: 'var(--radius-sm)', marginBottom: '1rem', border: '1px solid var(--moss-100)' }}>
          Click anywhere on the map to place a tree marker
        </div>
      )}

      <div style={{ position: 'relative', borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1px solid var(--border-md)', height: 680 }}>
        <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

        {bgImage && (
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 2 }}>
            <img src={bgImage} alt="Site plan overlay"
              style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.6, mixBlendMode: 'multiply' }} />
          </div>
        )}

        <div style={{ position: 'absolute', inset: 0, zIndex: 3, cursor: mode === 'place' ? 'crosshair' : 'default' }}
          onClick={handleCanvasClick}>
          {renderedMarkers.map(m => (
            <TreeMarker key={m.id} marker={m} selected={selected === m.id}
              onClick={mk => setSelected(s => s === mk.id ? null : mk.id)} />
          ))}
        </div>

        {selectedMarker && (
          <TreeDetail
            marker={selectedMarker}
            plant={selectedPlant}
            onDelete={id => { dispatch({ type: 'DELETE_TREE_MARKER', id }); setSelected(null); }}
            onClose={() => setSelected(null)}
          />
        )}
      </div>

      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleBgUpload} />

      {showBgUpload && (
        <Modal title="Upload site plan or screenshot" onClose={() => setShowBgUpload(false)} width={420}>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: '1rem', lineHeight: 1.6 }}>
            Take a screenshot of your plot in Google Maps satellite view, then upload it here. It'll appear as a semi-transparent overlay on the map so you can trace your trees accurately.
          </p>
          <Btn variant="primary" onClick={() => fileRef.current?.click()}>Choose image</Btn>
          {bgImage && (
            <div style={{ marginTop: '1rem' }}>
              <Btn variant="danger" size="sm" onClick={() => { setBgImage(null); dispatch({ type: 'SET_TREE_MAP_BG', url: null }); setShowBgUpload(false); }}>Remove overlay</Btn>
            </div>
          )}
        </Modal>
      )}

      {placing && (
        <AddTreeModal
          plants={state.plants}
          position={placing}
          onSave={marker => {
            dispatch({ type: 'ADD_TREE_MARKER', marker });
            setPlacing(null);
            setMode('view');
          }}
          onClose={() => setPlacing(null)}
        />
      )}
    </div>
  );
}
